import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FastForward, RotateCcw } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, useSave } from "@ncp/ui";

interface DemoClock { now: string; offsetHours: number }
interface Sweep { scanned: number; atRisk: number; breached: number; fired: number }

/**
 * The test platform's clock, where the host allows moving it (never on the production host: `/api/demo` is not mapped there). `null`
 * while unknown or unavailable, so nothing about it is shown.
 */
export function useDemoClock() {
  const api = useApi();
  const clock = useQuery({ queryKey: ["demo-clock"], queryFn: () => api.get<DemoClock>("/api/demo/clock"), refetchInterval: 60000, retry: false });
  return clock.isSuccess ? clock.data : null;
}

/** Moves the test platform's clock forward so statutory periods expire and reminders fire, then back to real time. */
export function DemoClockDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const clock = useDemoClock();
  const advance = useSave({
    mutationFn: (days: number) => api.post<{ now: string; offsetHours: number; sweep: Sweep }>("/api/demo/clock/advance", { days, hours: 0 }),
    saved: (r, days) => ({ title: t("backoffice:demo.advanced", { count: days }), description: t("backoffice:demo.sweepResult", { atRisk: r.sweep.atRisk, breached: r.sweep.breached, fired: r.sweep.fired }) }),
    onSuccess: () => { void qc.invalidateQueries(); },
  });
  const reset = useSave({ mutationFn: () => api.post("/api/demo/clock/reset", {}), saved: t("backoffice:demo.reset"), onSuccess: () => { void qc.invalidateQueries(); } });
  const offset = clock?.offsetHours ?? 0;
  const travelling = Math.abs(offset) >= 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t("backoffice:demo.title")}</DialogTitle>
          <DialogDescription>{t("backoffice:demo.hint")}</DialogDescription>
        </DialogHeader>
        <dl className="flex items-baseline justify-between gap-4 border-y border-sand-200 py-3 text-sm">
          <dt className="text-muted-foreground">{t("backoffice:demo.platformTime")}</dt>
          <dd className="tabular font-medium">{clock ? formatDateTime(clock.now, locale) : "—"}{travelling ? <span className="ms-2 text-warning-700">{t("backoffice:demo.offset", { count: Math.round(offset / 24) })}</span> : null}</dd>
        </dl>
        <div className="grid grid-cols-3 gap-2">
          {[1, 7, 30].map((d) => <Button key={d} size="sm" variant="outline" loading={advance.isPending && advance.variables === d} onClick={() => advance.mutate(d)}><FastForward /> {t("backoffice:demo.advanceBy", { count: d })}</Button>)}
        </div>
        <Button size="sm" variant="ghost" disabled={!travelling} loading={reset.isPending} onClick={() => reset.mutate()}><RotateCcw /> {t("backoffice:demo.resetClock")}</Button>
      </DialogContent>
    </Dialog>
  );
}
